import React, { useState, useEffect, useCallback, useRef } from 'react';
import BottomSheet from '../../../../components/mobile/BottomSheet';
import discoveryActions from '../../../../actions/discovery';
import BeforeAfterPreview, { AnimationVariant } from '../../../../components/BeforeAfterPreview';

const PRIMARY = '#FF7E67';

/**
 * Normalize item to model-like structure for consistency
 */
const normalizeItem = (item) => {
  if (item.item_type === 'workflow') {
    return {
      ...item,
      model_id: item.workflow_definition_id,
      model_name: item.name,
      model_type: 'workflow',
      model_description: item.description,
      model_cover_image_url: item.workflow_cover_image_url,
      model_cover_2_image_url: item.workflow_cover_2_image_url,
      isWorkflow: true
    };
  }
  return { ...item, isWorkflow: false };
};

/**
 * Mobile-optimized model/workflow selector using bottom sheet
 * Uses discovery API for curated trending, recent, categories view
 */
export default function ModelSelectorSheet({
  isOpen,
  onClose,
  selectedModel,
  onSelectModel,
  formatCost,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'models', 'workflows'
  const [loading, setLoading] = useState(false);
  const [discoveryData, setDiscoveryData] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const searchTimeoutRef = useRef(null);
  
  // Expanded section state
  const [expandedSection, setExpandedSection] = useState(null); // { slug, title }
  const [sectionItems, setSectionItems] = useState([]);
  const [sectionPagination, setSectionPagination] = useState(null);
  const [sectionLoading, setSectionLoading] = useState(false);

  // Fetch discovery data when sheet opens or tab changes
  useEffect(() => {
    if (isOpen && !searchQuery) {
      loadDiscoveryData();
    }
  }, [isOpen, activeTab]);

  const loadDiscoveryData = async () => {
    setLoading(true);
    setExpandedSection(null); // Reset expanded view
    try {
      const res = await discoveryActions.getDiscoveryData({ type: activeTab, zenEnabled: true });
      if (res.success) {
        setDiscoveryData(res.data);
      }
    } catch (err) {
      console.error('Failed to load discovery data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load section items with pagination
  const loadSectionItems = async (slug, title, page = 1) => {
    setSectionLoading(true);
    try {
      const res = await discoveryActions.getSectionItems(slug, { 
        type: activeTab, 
        page, 
        limit: 20, 
        zenEnabled: true 
      });
      if (res.success) {
        if (page === 1) {
          setSectionItems(res.data.items);
        } else {
          setSectionItems(prev => [...prev, ...res.data.items]);
        }
        setSectionPagination(res.data.pagination);
        setExpandedSection({ slug, title });
      }
    } catch (err) {
      console.error('Failed to load section items:', err);
    } finally {
      setSectionLoading(false);
    }
  };

  // Handle "See More" click
  const handleSeeMore = (slug, title) => {
    loadSectionItems(slug, title, 1);
  };

  // Handle "Load More" in expanded view
  const handleLoadMore = () => {
    if (expandedSection && sectionPagination?.hasMore) {
      loadSectionItems(expandedSection.slug, expandedSection.title, sectionPagination.page + 1);
    }
  };

  // Back to discovery view
  const handleBackToDiscovery = () => {
    setExpandedSection(null);
    setSectionItems([]);
    setSectionPagination(null);
  };

  // Debounced API search
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value.trim()) {
      setSearchResults(null);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await discoveryActions.searchDiscovery(value, activeTab, true);
        if (res.success && res.data.results) {
          setSearchResults(res.data.results.map(normalizeItem));
        }
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [activeTab]);

  const handleSelect = (item) => {
    const normalized = normalizeItem(item);
    onSelectModel(normalized);
    onClose();
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery('');
    setSearchResults(null);
  };

  // Clear state when sheet closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults(null);
    }
  }, [isOpen]);

  // Render search results view
  const renderSearchResults = () => {
    if (!searchResults || searchResults.length === 0) {
      return (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
          No results found for "{searchQuery}"
        </div>
      );
    }
    return (
      <div style={{ padding: '0 12px' }}>
        {searchResults.map(item => (
          <ModelItem
            key={item.model_id || item.workflow_definition_id}
            model={item}
            isSelected={selectedModel?.model_id === (item.model_id || item.workflow_definition_id)}
            onSelect={() => handleSelect(item)}
            formatCost={formatCost}
            isWorkflow={item.isWorkflow || item.item_type === 'workflow'}
          />
        ))}
      </div>
    );
  };

  // Render expanded section view with all items + pagination
  const renderExpandedSection = () => {
    return (
      <div style={{ padding: '0 12px' }}>
        {/* Back button */}
        <button
          onClick={handleBackToDiscovery}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 0',
            background: 'none',
            border: 'none',
            color: PRIMARY,
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            marginBottom: 8,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>

        {/* Section title */}
        <div style={{ 
          padding: '4px 0 12px', 
          fontSize: 15, 
          fontWeight: 600, 
          color: '#fff',
        }}>
          {expandedSection?.title}
          {sectionPagination && (
            <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
              ({sectionPagination.total} items)
            </span>
          )}
        </div>

        {/* Items */}
        {sectionItems.map(item => {
          const itemId = item.model_id || item.workflow_definition_id;
          const isWorkflow = item.item_type === 'workflow' || item.isWorkflow;
          return (
            <ModelItem
              key={itemId}
              model={normalizeItem(item)}
              isSelected={selectedModel?.model_id === itemId}
              onSelect={() => handleSelect(item)}
              formatCost={formatCost}
              isWorkflow={isWorkflow}
            />
          );
        })}

        {/* Load More button */}
        {sectionPagination?.hasMore && (
          <button
            onClick={handleLoadMore}
            disabled={sectionLoading}
            style={{
              width: '100%',
              padding: '12px',
              marginTop: 8,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 10,
              color: '#9ca3af',
              fontSize: 13,
              fontWeight: 500,
              cursor: sectionLoading ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            {sectionLoading ? (
              <>
                <div style={{ 
                  width: 14, 
                  height: 14, 
                  border: '2px solid rgba(255,255,255,0.1)', 
                  borderTopColor: PRIMARY,
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
                Loading...
              </>
            ) : (
              <>Load More ({sectionPagination.total - sectionItems.length} remaining)</>
            )}
          </button>
        )}
      </div>
    );
  };

  // Render curated discovery view (trending, recent, categories)
  const renderDiscoveryView = () => {
    if (!discoveryData) return null;
    const { trending, recent, categories } = discoveryData;
    
    // Separate "More to Explore" from other categories
    const regularCategories = categories?.filter(cat => cat.slug !== 'more-to-explore') || [];
    const moreToExplore = categories?.find(cat => cat.slug === 'more-to-explore');

    return (
      <div>
        {/* Trending Section - Horizontal Carousel */}
        {trending && trending.length > 0 && (
          <HorizontalSection 
            title="🔥 Trending" 
            slug="trending"
            items={trending} 
            onSelect={handleSelect} 
            onSeeMore={handleSeeMore}
            selectedModel={selectedModel} 
            formatCost={formatCost} 
          />
        )}

        {/* Recently Added Section - Horizontal Carousel */}
        {recent && recent.length > 0 && (
          <HorizontalSection 
            title="✨ Recently Added" 
            slug="recent"
            items={recent} 
            onSelect={handleSelect} 
            onSeeMore={handleSeeMore}
            selectedModel={selectedModel} 
            formatCost={formatCost} 
          />
        )}

        {/* Regular Categories - Horizontal Carousels */}
        {regularCategories.map(cat => (
          cat.items && cat.items.length > 0 && (
            <HorizontalSection 
              key={cat.category_id} 
              title={`${cat.icon || '📁'} ${cat.name}`} 
              slug={cat.slug}
              items={cat.items} 
              onSelect={handleSelect} 
              onSeeMore={handleSeeMore}
              selectedModel={selectedModel} 
              formatCost={formatCost}
            />
          )
        ))}
        
        {/* More to Explore - Vertical List */}
        {moreToExplore && moreToExplore.items && moreToExplore.items.length > 0 && (
          <div style={{ padding: '0 12px' }}>
            <VerticalSection 
              title="🌟 More to Explore" 
              slug="more-to-explore"
              items={moreToExplore.items} 
              onSelect={handleSelect} 
              onSeeMore={handleSeeMore}
              selectedModel={selectedModel} 
              formatCost={formatCost}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Select Model"
    >
      {/* Search */}
      <div style={{ padding: '8px 16px 12px' }}>
        <div style={{
          background: 'rgba(255,255,255,0.06)',
          borderRadius: 12,
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          border: '1px solid rgba(255,255,255,0.08)'
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search models & workflows..."
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#fff',
              fontSize: 16, // 16px prevents iOS zoom on focus
            }}
          />
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(''); setSearchResults(null); }}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                padding: 4,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          )}
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{
        display: 'flex',
        gap: 8,
        padding: '0 16px 12px',
      }}>
        {[
          { key: 'all', label: 'All' },
          { key: 'models', label: 'Models' },
          { key: 'workflows', label: 'Workflows' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            style={{
              flex: 1,
              padding: '8px 12px',
              background: activeTab === tab.key ? PRIMARY : 'rgba(255,255,255,0.06)',
              border: activeTab === tab.key ? `1px solid ${PRIMARY}` : '1px solid rgba(255,255,255,0.08)',
              borderRadius: 8,
              color: activeTab === tab.key ? '#fff' : '#9ca3af',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
          <div style={{ 
            width: 24, 
            height: 24, 
            border: '2px solid rgba(255,255,255,0.1)', 
            borderTopColor: PRIMARY,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 12px'
          }} />
          {searchQuery ? 'Searching...' : 'Loading...'}
        </div>
      ) : searchQuery && searchResults !== null ? (
        renderSearchResults()
      ) : expandedSection ? (
        renderExpandedSection()
      ) : (
        renderDiscoveryView()
      )}
      
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </BottomSheet>
  );
}

/** Get images for an item with priority */
function getItemImages(item) {
  const isWorkflow = item.item_type === 'workflow' || item.isWorkflow;
  
  if (isWorkflow) {
    const img1 = item.workflow_cover_2_image_url || item.workflow_cover_image_url;
    const img2 = item.workflow_cover_image_url !== item.workflow_cover_2_image_url 
      ? item.workflow_cover_image_url 
      : null;
    return { primary: img1, secondary: img2 && img1 !== img2 ? img2 : null };
  }
  
  // Model priority: model_cover_2_image_url || model_cover_image_url || model_thumbnail_image_url
  const img1 = item.model_cover_2_image_url || item.model_cover_image_url || item.model_thumbnail_image_url;
  const img2 = item.model_cover_image_url && item.model_cover_2_image_url && 
               item.model_cover_image_url !== item.model_cover_2_image_url
    ? item.model_cover_image_url 
    : null;
  return { primary: img1, secondary: img2 };
}

/** Horizontal carousel section for Trending, Recent, Categories */
function HorizontalSection({ title, slug, items, onSelect, onSeeMore, selectedModel, formatCost }) {
  const scrollRef = useRef(null);
  
  return (
    <div style={{ marginBottom: 24 }}>
      {/* Header */}
      <div style={{ 
        padding: '0 16px 10px', 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{title}</span>
        <button
          onClick={() => onSeeMore(slug, title)}
          style={{
            background: 'none',
            border: 'none',
            color: PRIMARY,
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
            padding: '2px 0',
          }}
        >
          See All →
        </button>
      </div>
      
      {/* Horizontal scroll container */}
      <div 
        ref={scrollRef}
        style={{
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 4,
          // Hide scrollbar
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {/* Inner flex container with padding */}
        <div style={{
          display: 'inline-flex',
          gap: 12,
          paddingLeft: 16,
          paddingRight: 16,
        }}>
          {items.slice(0, 8).map((item) => {
            const itemId = item.model_id || item.workflow_definition_id;
            const isWorkflow = item.item_type === 'workflow' || item.isWorkflow;
            const images = getItemImages(item);
            const normalized = normalizeItem(item);
            
            return (
              <CarouselCard
                key={itemId}
                item={normalized}
                images={images}
                isSelected={selectedModel?.model_id === itemId}
                onSelect={() => onSelect(item)}
                formatCost={formatCost}
                isWorkflow={isWorkflow}
              />
            );
          })}
        </div>
      </div>
      
      {/* Hide scrollbar CSS */}
      <style>{`
        div::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}

/** Rich carousel card with before/after slider */
function CarouselCard({ item, images, isSelected, onSelect, formatCost, isWorkflow }) {
  const hasSlider = images.primary && images.secondary;
  
  return (
    <button
      onClick={onSelect}
      style={{
        flexShrink: 0,
        width: 160,
        scrollSnapAlign: 'start',
        background: isSelected ? 'rgba(250, 139, 20, 0.12)' : 'rgba(255,255,255,0.04)',
        border: isSelected ? `2px solid ${PRIMARY}` : '1px solid rgba(255,255,255,0.1)',
        borderRadius: 16,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        textAlign: 'left',
        padding: 0,
      }}
    >
      {/* Image area */}
      <div 
        style={{
          position: 'relative',
          width: '100%',
          height: 120,
          background: 'rgba(0,0,0,0.3)',
          overflow: 'hidden',
        }}
      >
        {/* Before/After Preview or Single Image */}
        {hasSlider ? (
          <BeforeAfterPreview
            before={images.primary}
            after={images.secondary}
          animationVarient={AnimationVariant.SLIDE}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
            }}
          />
        ) : (
          <img
            src={images.primary || '/placeholder.png'}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={(e) => e.target.style.opacity = 0}
          />
        )}
        
        {/* Workflow badge */}
        {isWorkflow && (
          <div style={{
            position: 'absolute',
            top: 8,
            left: 8,
            fontSize: 9,
            padding: '3px 6px',
            borderRadius: 4,
            background: 'rgba(147, 51, 234, 0.85)',
            color: '#fff',
            fontWeight: 600,
            backdropFilter: 'blur(4px)',
            zIndex: 2,
          }}>
            Workflow
          </div>
        )}
        
        {/* Selected checkmark */}
        {isSelected && (
          <div style={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 22,
            height: 22,
            borderRadius: 11,
            background: PRIMARY,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
        )}
      </div>
      
      {/* Info */}
      <div style={{ padding: '10px 12px 12px' }}>
        <div style={{
          color: isSelected ? PRIMARY : '#fff',
          fontSize: 13,
          fontWeight: 600,
          marginBottom: 4,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {item.model_name}
        </div>
        <div style={{ 
          color: '#6b7280', 
          fontSize: 11,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          {isWorkflow ? (
            <span>Multi-step</span>
          ) : (
            <>
              <span>~{formatCost ? formatCost(item.average_cost) : item.average_cost} URO</span>
              {item.model_eta && (
                <>
                  <span style={{ color: '#4b5563' }}>•</span>
                  <span>{item.model_eta}s</span>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </button>
  );
}

/** Vertical section for "More to Explore" */
function VerticalSection({ title, slug, items, onSelect, onSeeMore, selectedModel, formatCost }) {
  return (
    <div style={{ marginBottom: 20, padding: '0 4px' }}>
      <div style={{ 
        padding: '8px 0', 
        fontSize: 15, 
        fontWeight: 600, 
        color: '#fff',
        marginBottom: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span>{title}</span>
        <button
          onClick={() => onSeeMore(slug, title)}
          style={{
            background: 'none',
            border: 'none',
            color: PRIMARY,
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
            padding: '2px 0',
          }}
        >
          See All →
        </button>
      </div>
      {items.map(item => {
        const itemId = item.model_id || item.workflow_definition_id;
        const isWorkflow = item.item_type === 'workflow' || item.isWorkflow;
        return (
          <ModelItem
            key={itemId}
            model={normalizeItem(item)}
            isSelected={selectedModel?.model_id === itemId}
            onSelect={() => onSelect(item)}
            formatCost={formatCost}
            isWorkflow={isWorkflow}
          />
        );
      })}
    </div>
  );
}

function ModelItem({ model, isSelected, onSelect, formatCost, isWorkflow }) {
  return (
    <button
      onClick={onSelect}
      style={{
        width: '100%',
        padding: 12,
        marginBottom: 6,
        background: isSelected ? 'rgba(250, 139, 20, 0.1)' : 'rgba(255,255,255,0.04)',
        border: isSelected ? `1px solid ${PRIMARY}` : '1px solid rgba(255,255,255,0.08)',
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
    >
      {/* Thumbnail */}
      <div style={{
        width: 48,
        height: 48,
        borderRadius: 8,
        background: 'rgba(255,255,255,0.1)',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        <img
          src={model.model_cover_2_image_url || model.workflow_cover_2_image_url || model.model_cover_image_url || '/placeholder.png'}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => e.target.style.display = 'none'}
        />
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 2
        }}>
          <span style={{
            color: isSelected ? PRIMARY : '#fff',
            fontSize: 14,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {model.model_name}
          </span>
          {isWorkflow && (
            <span style={{
              fontSize: 10,
              padding: '2px 6px',
              borderRadius: 4,
              background: 'rgba(147, 51, 234, 0.2)',
              color: '#a78bfa',
              fontWeight: 500,
            }}>
              Workflow
            </span>
          )}
        </div>
        <div style={{ color: '#6b7280', fontSize: 12 }}>
          {isWorkflow 
            ? 'Predefined inputs · prompt optional' 
            : `~${formatCost ? formatCost(model.average_cost) : model.average_cost} URO`}
          {model.model_eta && ` • ${model.model_eta}s`}
        </div>
      </div>

      {/* Selected indicator */}
      {isSelected && (
        <div style={{
          width: 24,
          height: 24,
          borderRadius: 12,
          background: PRIMARY,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
      )}
    </button>
  );
}
